import api from './api';
import { Post, PostComment, PostLiker } from '../types';

const POSTS_CHANGED_EVENT = 'intranet:postsChanged';
const emitPostsChanged = () => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(POSTS_CHANGED_EVENT));
};

export interface CreatePostData {
  caption: string;
  content?: string;
  category:
    | 'POST'
    | 'ANNOUNCEMENT'
    | 'POLICY'
    | 'SPOTLIGHT_EOM'
    | 'SPOTLIGHT_NEW_EMPLOYEE';
  cdnLink?: string;
  department?: string;
  accessScope?: 'COMPANY' | 'BRANCH' | 'DEPARTMENT' | 'DEPARTMENT_ONLY';
  branch?: string;
  isNewEmployee?: boolean;
  shouldNotify?: boolean;
  employeeName?: string;
  employeeDepartment?: string;
  eomHeaderLabel?: string;
  eomCtaText?: string;
  eomLikes?: number;
  eomComments?: number;
  eomShares?: number;
};

export const postsService = {
  /** Full visible feed for the current user (no limit/cursor). */
  async getAll(): Promise<Post[]> {
    const response = await api.get<Post[] | { items: Post[] }>('/posts');
    const data = response.data;
    if (Array.isArray(data)) return data;
    return Array.isArray(data?.items) ? data.items : [];
  },

  async getById(id: string): Promise<Post> {
    const response = await api.get(`/posts/${id}`);
    return response.data;
  },

  async uploadImage(data: FormData): Promise<{ url: string }> {
    const response = await api.post('/posts/upload', data);
    return response.data;
  },

  async createPost(data: CreatePostData): Promise<Post> {
    const response = await api.post('/posts', data);
    emitPostsChanged();
    return response.data;
  },

  async create(data: FormData): Promise<Post> {
    const response = await api.post('/posts', data);
    emitPostsChanged();
    return response.data;
  },

  async update(id: string, data: Partial<Post>): Promise<Post> {
    const response = await api.patch(`/posts/${id}`, data);
    emitPostsChanged();
    return response.data;
  },

  async delete(id: string): Promise<void> {
    await api.delete(`/posts/${id}`);
    emitPostsChanged();
  },

  async toggleLike(postId: string): Promise<{ liked: boolean; likeCount: number }> {
    const response = await api.post(`/posts/${postId}/like`);
    return response.data;
  },

  async getLikers(postId: string): Promise<PostLiker[]> {
    const response = await api.get(`/posts/${postId}/likes`);
    return response.data;
  },

  async getComments(postId: string): Promise<PostComment[]> {
    const response = await api.get(`/posts/${postId}/comments`);
    return response.data;
  },

  async addComment(postId: string, text: string): Promise<PostComment> {
    const response = await api.post(`/posts/${postId}/comments`, { text });
    return response.data;
  },

  async deleteComment(postId: string, commentId: string): Promise<void> {
    await api.delete(`/posts/${postId}/comments/${commentId}`);
  },
};
